import { useReducer, useMemo, useCallback, useRef, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import type { UseFormReturn } from 'react-hook-form';
import { ActionRegistry } from '@packages/table-builder/registry';
import type { FormRendererProps } from '../types/renderer';
import type { FormLayoutNode, FormFieldsNode, FormConditionalNode, FormTabsNode } from '../types/layout';
import type { BlockSlot, FormEffect, FormObserver, FormRule } from '../types/block';
import type { FormFieldMapping } from '../types/fieldMapping';
import type { FormEngineStatus } from '../store/engineReducer';
import { engineReducer, initialEngineState } from '../store/engineReducer';
import { resolveMode } from '../processors/modeResolver';
import { mergeBlocks } from '../processors/blockMerger';
import { composeValidation } from '../processors/validationComposer';
import { resolveRulesTokens } from '../resolver/tokenResolver';
import { useEffectProcessor } from '../processors/effectProcessor';
import { useObserverProcessor } from '../processors/observerProcessor';
import type { ProgrammaticWriteGuardRef } from '../processors/programmaticWriteGuard';
import { useLifecycle } from '../processors/lifecycleProcessor';
import { schedulePostSubmitFormReset } from '../utils/postSubmitFormReset';
import { syncDerivedEffects } from '../processors/syncDerivedEffects';
import { sanitizePayload, toSubmitActionParams } from '../processors/payloadSanitizer';
import { useStepProcessor } from '../processors/stepProcessor';
import { ResolverRegistry } from '../resolvers/resolverRegistry';
import { useFormHistory } from '../history/useFormHistory';
import type { StepProcessorReturn } from '../processors/stepProcessor';
import type { BlockOverrides } from '../types/block';
import type { FormHistoryAPI } from '../history/useFormHistory';
import { defaultCustomRenderers } from '../renderers/defaultCustomRenderers';
import { mergeFormEngineMeta } from '../utils/mergeFormEngineMeta';
import { useComposeFormSession } from '../session/useComposeFormSession';

function flattenErrorKeys(
  errors: Record<string, unknown>,
  prefix = '',
): string[] {
  const keys: string[] = [];
  for (const [k, v] of Object.entries(errors)) {
    const path = prefix ? `${prefix}.${k}` : k;
    if (v && typeof v === 'object' && 'message' in (v as Record<string, unknown>)) {
      keys.push(path);
    } else if (v && typeof v === 'object') {
      keys.push(...flattenErrorKeys(v as Record<string, unknown>, path));
    }
  }
  return keys;
}

function collectFieldDefaults(
  nodes: FormLayoutNode[],
  result: Record<string, unknown> = {},
): Record<string, unknown> {
  for (const node of nodes) {
    switch (node.type) {
      case 'fields': {
        const fieldsNode = node as FormFieldsNode;
        for (const entry of fieldsNode.fields) {
          if (!('type' in entry)) {
            // Plain FormFieldMapping — no type discriminator
          } else {
            const entryType = (entry as { type: string }).type;
            if (entryType === 'spacer' || entryType === 'section-divider' || entryType === 'inline-action-button') continue;
            if (entryType === 'stack') {
              // FormFieldStackEntry — recurse into its nested fields
              const stackEntry = entry as import('../types/layout').FormFieldStackEntry;
              for (const stackField of stackEntry.fields) {
                if (stackField.defaultValue !== undefined) {
                  setNestedDefaultValue(result, stackField.name, stackField.defaultValue);
                } else if (!(stackField.name in result)) {
                  setNestedDefaultValue(result, stackField.name, null);
                }
              }
              continue;
            }
            if (entryType === 'each') continue; // FormInlineEachEntry — handled by its own layout node
          }
          const field = entry as FormFieldMapping;
          if (field.defaultValue !== undefined) {
            setNestedDefaultValue(result, field.name, field.defaultValue);
          } else if (!(field.name in result)) {
            // Seed every field with null so RHF registers it in defaultValues.
            // Without this, repeatable rows (items[]) are sparse — only fields
            // with explicit defaultValue appear, and form.getValues() omits the
            // rest, producing incomplete API payloads.
            setNestedDefaultValue(result, field.name, null);
          }
        }
        break;
      }
      case 'section':
      case 'grid':
      case 'stack':
      case 'surface':
        collectFieldDefaults((node as { children: FormLayoutNode[] }).children, result);
        break;
      case 'tabs': {
        const tabsNode = node as FormTabsNode;
        for (const tab of tabsNode.tabs) collectFieldDefaults(tab.children, result);
        break;
      }
      case 'conditional': {
        const condNode = node as FormConditionalNode;
        collectFieldDefaults(condNode.children, result);
        if (condNode.else) collectFieldDefaults(condNode.else, result);
        break;
      }
      case 'column-group':
        // FormColumnGroupItem.fields: (FormFieldMapping | FormSectionDividerEntry | FormInlineEachEntry)[]
        // 'stack' is not part of this union — no stack branch needed here.
        for (const group of node.groups) {
          for (const entry of group.fields) {
            if ('type' in entry) continue; // FormSectionDividerEntry | FormInlineEachEntry — skip
            const field = entry as FormFieldMapping;
            if (field.defaultValue !== undefined) {
              setNestedDefaultValue(result, field.name, field.defaultValue);
            } else if (!(field.name in result)) {
              setNestedDefaultValue(result, field.name, null);
            }
          }
        }
        break;
      default:
        break;
    }
  }
  return result;
}

/** Write `value` at `dotPath` into `obj`, creating intermediate objects as needed. */
function setNestedDefaultValue(
  obj: Record<string, unknown>,
  dotPath: string,
  value: unknown,
): void {
  const parts = dotPath.split('.');
  const leaf = parts.pop()!;
  let current = obj;
  for (const key of parts) {
    if (current[key] == null || typeof current[key] !== 'object') {
      current[key] = {};
    }
    current = current[key] as Record<string, unknown>;
  }
  current[leaf] = value;
}

/**
 * Shallow-deep merge: source keys go in first, then target keys overwrite.
 * Only one level of nesting is merged; nested objects at the same key are spread shallowly.
 */
function deepMerge(
  source: Record<string, unknown>,
  target: Record<string, unknown>,
): Record<string, unknown> {
  const result: Record<string, unknown> = { ...source };
  for (const [key, targetVal] of Object.entries(target)) {
    const sourceVal = result[key];
    if (
      targetVal != null &&
      typeof targetVal === 'object' &&
      !Array.isArray(targetVal) &&
      sourceVal != null &&
      typeof sourceVal === 'object' &&
      !Array.isArray(sourceVal)
    ) {
      result[key] = deepMerge(
        sourceVal as Record<string, unknown>,
        targetVal as Record<string, unknown>,
      );
    } else {
      result[key] = targetVal;
    }
  }
  return result;
}

/**
 * Sort blocks by BlockSlot.order and/or mode-level blockOrder.
 * blockOrder (string[]) takes precedence — listed IDs render first in that order.
 * Then BlockSlot.order (lower first). Blocks without either preserve original position.
 */
function applyBlockOrder(blocks: BlockSlot[], blockOrder?: string[]): BlockSlot[] {
  if (!blockOrder?.length && !blocks.some((b) => b.order != null)) return blocks;

  const sorted = [...blocks];

  if (blockOrder?.length) {
    sorted.sort((a, b) => {
      const aIdx = blockOrder.indexOf(a.use.id);
      const bIdx = blockOrder.indexOf(b.use.id);
      if (aIdx !== -1 && bIdx !== -1) return aIdx - bIdx;
      if (aIdx !== -1) return -1;
      if (bIdx !== -1) return 1;
      return 0;
    });
  } else {
    sorted.sort((a, b) => {
      const aOrder = a.order ?? Infinity;
      const bOrder = b.order ?? Infinity;
      return aOrder - bOrder;
    });
  }

  return sorted;
}

export interface FormEngineSessionReturn {
  form: UseFormReturn<Record<string, unknown>>;
  layout: FormLayoutNode[];
  effects: FormEffect[];
  observers: FormObserver[];
  status: FormEngineStatus;
  dispatch: React.Dispatch<import('../store/engineReducer').EngineAction>;
  meta: Record<string, unknown>;
  isReadOnly: boolean;
  viewPresentation: import('../types/schema').ViewPresentation;
  isWizard: boolean;
  modeName: string;
  modeOverrides: import('../types/schema').ModeOverrides | undefined;
  getOptionProp: (field: string, prop: string) => unknown;
  setOptionMeta: (fieldName: string, value: string, metadata: Record<string, unknown>) => void;
  customRenderers: FormRendererProps['customRenderers'];
  onFormSubmit: (e?: React.BaseSyntheticEvent) => Promise<void>;
  /** Lifecycle submit that takes validated values and returns the API result. Used by wizard integration. */
  onFormSubmitInternal: (values: Record<string, unknown>) => Promise<unknown>;
  stepProcessor: StepProcessorReturn | null;
  stepLayout: FormLayoutNode[] | null;
  mode: ReturnType<typeof resolveMode>['mode'];
  udfRequiredFieldsRef: React.RefObject<Map<string, string>>;
  blockOverrides?: BlockOverrides;
  formHistory: FormHistoryAPI | null;
  namespace?: string;
  hydratingRef: React.RefObject<boolean>;
  hydrationEpochRef: React.RefObject<number>;
  programmaticWriteGuardRef: ProgrammaticWriteGuardRef;
  formSessionKey: string;
}

export function useFormEngineSession({
  schema,
  mode: modeName,
  params,
  meta: externalMeta = {},
  onSuccess,
  onError,
  onFatalError,
  onSubmitCapture,
  resolvers,
  customRenderers,
  actions,
  isReadOnly: isReadOnlyOverride,
  viewPresentation: viewPresentationOverride,
  initialValues,
  supplementalFetch,
  onSubmitSuccess,
  onSubmitFailure,
  eventScope,
  semanticId,
}: FormRendererProps): FormEngineSessionReturn {
  const { mode, isReadOnly: modeIsReadOnly, viewPresentation: modeViewPresentation } = useMemo(() => resolveMode(schema, modeName), [schema, modeName]);

  const [engineState, dispatch] = useReducer(
    engineReducer,
    (!initialValues && Boolean(mode.dataSource?.fetch)) ? { status: 'fetching' as const, error: null } : initialEngineState,
  );
  const optionStoreRef = useRef<Map<string, Map<string, Record<string, unknown>>>>(new Map());

  // Caller can force read-only (e.g. Studio "View" preview, detail pages) without needing a view mode in the schema.
  const isReadOnly = isReadOnlyOverride ?? modeIsReadOnly;
  const viewPresentation = viewPresentationOverride ?? modeViewPresentation;

  const defaultNamespace = schema.features?.namespace;

  useEffect(() => {
    if (defaultNamespace) {
      ActionRegistry.ensureDomain(defaultNamespace);
    }
  }, [defaultNamespace]);

  const isWizard = Boolean(schema.steps && schema.steps.length > 0);
  const blocks: BlockSlot[] = useMemo(() => {
    const raw = schema.blocks ?? [];
    if (!defaultNamespace) return raw;
    return raw.map((slot) => ({ ...slot, namespace: slot.namespace ?? defaultNamespace }));
  }, [schema.blocks, defaultNamespace]);

  const schemaRules: FormRule[] = useMemo(() => {
    const raw = schema.rules ?? [];
    return defaultNamespace ? resolveRulesTokens(raw, defaultNamespace) : raw;
  }, [schema.rules, defaultNamespace]);

  const { layout, effects, observers, rules, blockOverrides } = useMemo(() => {
    const sourceBlocks = isWizard ? (schema.steps ?? []).flatMap((step) => step.blocks) : blocks;

    const orderedBlocks = applyBlockOrder(sourceBlocks, mode.blockOrder);
    const merged = mergeBlocks(orderedBlocks, params?.id);

    return {
      ...merged,
      effects: [...merged.effects, ...(schema.effects ?? [])],
      observers: [...merged.observers, ...(schema.observers ?? [])],
      rules: [...merged.rules, ...schemaRules],
    };
  }, [schema, blocks, params?.id, isWizard, schemaRules, mode.blockOrder]);

  const formSessionKey = useComposeFormSession({
    schema,
    layout,
    mode: modeName,
    params,
    meta: externalMeta,
  });

  const isRepeatable = Boolean(mode.repeatable);

  const [stepMeta, setStepMeta] = useState<Record<string, unknown>>({});
  const meta: Record<string, unknown> = useMemo(
    () => mergeFormEngineMeta(modeName, externalMeta, stepMeta),
    [externalMeta, stepMeta, modeName],
  );

  const udfRequiredFieldsRef = useRef<Map<string, string>>(new Map());

  const { schema: zodSchema } = useMemo(() => {
    // In repeatable mode the composed schema validates each array item.
    // UDF ref paths are stored as form-absolute (e.g. `items.0.userDefinedFields.0.value`).
    // Pass `udfItemPrefix` (truthy sentinel) so runUdfValidation strips `items.N.`
    // from paths before resolving against per-item data in the per-item superRefine.
    const udfItemPrefix = isRepeatable ? 'items' : undefined;
    const composed = composeValidation(layout, rules, udfRequiredFieldsRef, meta, udfItemPrefix);
    if (isRepeatable) {
      return {
        schema: z
          .object({
            items: z.array(composed.schema),
          })
          .passthrough() as any,
        fieldNames: composed.fieldNames,
      };
    }
    return { schema: composed.schema as any, fieldNames: composed.fieldNames };
  }, [layout, rules, isRepeatable, meta]);

  // Collect per-field `defaultValue` declarations from the layout tree.
  // These are merged *under* any explicit schema defaultValues so that schema-level
  // defaults always win, and on edit-hydration the API data overwrites both.
  const fieldDefaults = useMemo(() => collectFieldDefaults(layout), [layout]);

  // For repeatable mode: pre-seed `items` with minItems empty rows so
  // useFieldArray initializes with the right count — avoids the seeding
  // useEffect race (StrictMode unmount resets seededRef → infinite loop).
  const baseDefaultValues = useMemo(() => {
    // Wizard restore: initialValues with __wizardRestore flag must seed defaultValues
    // directly so useFieldArray and Controllers mount with the correct data.
    // Without this, form.reset() in useEffect runs AFTER controllers read empty defaults.
    const isWizardRestore = initialValues && (initialValues as Record<string, unknown>).__wizardRestore;
    if (isWizardRestore) {
      const { __wizardRestore: _, ...cleanWizardValues } = initialValues as Record<string, unknown> & { __wizardRestore?: boolean };
      return cleanWizardValues;
    }

    const merged = deepMerge(fieldDefaults, mode.defaultValues ?? {});
    if (isRepeatable && mode.repeatable) {
      const min = mode.repeatable.minItems ?? 1;
      const existingItems = Array.isArray(merged.items) ? merged.items : [];
      if (existingItems.length < min) {
        merged.items = [
          ...existingItems,
          ...Array.from({ length: min - existingItems.length }, () => ({ ...fieldDefaults })),
        ];
      }
    }
    return merged;
  }, [fieldDefaults, mode.defaultValues, isRepeatable, mode.repeatable, initialValues]);

  const form = useForm<Record<string, unknown>>({
    resolver: zodResolver(zodSchema),
    mode: 'onTouched',
    reValidateMode: 'onChange',
    shouldFocusError: false,
    defaultValues: baseDefaultValues,
  });

  const draftStorageKey =
    mode.lifecycle?.autoSave?.storageKey ?? `form-draft-${schema.metadata.semanticId}-${params?.id ?? 'new'}`;

  const formHistory = useFormHistory(
    form,
    schema.history?.enabled ? schema.history : undefined,
    {
      storageKey: draftStorageKey,
      schemaId: schema.metadata.semanticId,
      paramsId: params?.id,
      autoSaveEnabled: mode.lifecycle?.autoSave?.enabled,
      restorePrompt: mode.lifecycle?.autoSave?.restorePrompt,
    },
  );

  const getOptionProp = useCallback(
    (fieldName: string, prop: string): unknown => {
      const fieldOptions = optionStoreRef.current.get(fieldName);
      if (!fieldOptions) return undefined;
      const selectedValue = form.getValues(fieldName as any);
      const selectedOption = fieldOptions.get(String(selectedValue));
      return selectedOption?.[prop];
    },
    [form]
  );

  /** Latest effects for derived sync (create/edit parity) — updated after activeEffects computes. */
  const effectsRef = useRef<FormEffect[]>([]);
  const customResolversRef = useRef<Record<string, (ctx: import('../evaluators/conditionEvaluator').ConditionContext) => unknown> | undefined>(undefined);
  const programmaticWriteGuardRef = useRef<Set<string>>(new Set()) as ProgrammaticWriteGuardRef;

  const runDerivedSync = useCallback(
    (onlyWatchFields?: string[]) => {
      syncDerivedEffects({
        form: form as UseFormReturn<Record<string, unknown>>,
        effects: effectsRef.current,
        meta,
        getOptionProp,
        customResolvers: customResolversRef.current,
        onlyWatchFields,
        precisionConfig: schema.precision,
      });
    },
    [form, meta, getOptionProp, schema.precision],
  );

  const setOptionMeta = useCallback(
    (fieldName: string, value: string, metadata: Record<string, unknown>): void => {
      if (!optionStoreRef.current.has(fieldName)) {
        optionStoreRef.current.set(fieldName, new Map());
      }
      optionStoreRef.current.get(fieldName)!.set(value, metadata);
      // Options just became available for this field — sync optionProp-derived flags.
      queueMicrotask(() => runDerivedSync([fieldName]));
    },
    [runDerivedSync],
  );

  const onMetaUpdate = useCallback((updated: Record<string, unknown>) => {
    setStepMeta(prev => ({ ...prev, ...updated }));
  }, []);

  const onStepSubmit = useCallback(
    async (
      stepValues: Record<string, unknown>,
      stepId: string,
    ): Promise<Record<string, unknown> | void> => {
      const submitConfig =
        mode.dataSource?.stepSubmit?.[stepId] ?? mode.dataSource?.submit;
      if (!submitConfig?.actionId) return;

      const mergedValues = { ...form.getValues(), ...stepValues };
      const cleaned = sanitizePayload(mergedValues, layout, defaultNamespace);
      let actionParams = toSubmitActionParams(
        cleaned as Record<string, unknown> | Record<string, unknown>[],
        submitConfig.constants,
      );

      if (meta.id != null) {
        const body = actionParams.payload;
        if (!Array.isArray(body) && body.id == null) {
          actionParams = { payload: { ...body, id: meta.id } };
        }
      }

      const result = await ActionRegistry.executeData(
        submitConfig.actionId,
        actionParams as unknown as Record<string, string>,
        undefined,
        { mode: modeName as 'create' | 'edit' | 'view' },
      );

      const resultRecord = (result ?? {}) as Record<string, unknown>;
      if (resultRecord.success === false) {
        throw resultRecord;
      }

      return resultRecord;
    },
    [mode.dataSource, form, layout, defaultNamespace, meta.id, modeName],
  );

  const hydratingRef = useRef(false);
  const hydrationEpochRef = useRef(0);
  const triggerTimerRef = useRef<number | null>(null);

  /**
   * Cross-field re-validation after submit.
   *
   * Problem: RHF with `mode: 'onTouched'` only re-validates the changed field.
   * Cross-field conditional rules (e.g., "if p is filled, q is required") produce
   * errors on untouched siblings that become stale when the triggering field is
   * cleared. Users see phantom "Required" errors until the next submit.
   *
   * Fix: once `formState.isSubmitted` is true (errors are visible), subscribe to
   * all value changes via `form.watch()` and debounce a full `form.trigger()`.
   * This re-runs the entire Zod schema and refreshes ALL field errors.
   * The 150ms debounce coalesces rapid keystrokes into a single validation pass.
   */
  useEffect(() => {
    if (!form.formState.isSubmitted) return;
    const subscription = form.watch(() => {
      if (hydratingRef.current) return;
      if (triggerTimerRef.current != null) clearTimeout(triggerTimerRef.current);
      triggerTimerRef.current = window.setTimeout(() => {
        form.trigger();
        triggerTimerRef.current = null;
      }, 150);
    });
    return () => {
      subscription.unsubscribe();
      if (triggerTimerRef.current != null) {
        clearTimeout(triggerTimerRef.current);
        triggerTimerRef.current = null;
      }
    };
  }, [form, form.formState.isSubmitted]);

  const customResolvers = useMemo(() => {
    const registry = ResolverRegistry.getCustomResolvers();
    if (!resolvers) return registry;
    const merged: Record<string, (ctx: any) => unknown> = { ...registry };
    for (const [id, resolver] of Object.entries(resolvers)) {
      merged[id] = (ctx) => resolver({ values: ctx.values, meta: ctx.meta, field: undefined, getValues: ctx.getValues });
    }
    return merged;
  }, [resolvers]);

  const stepProcessorResult = useStepProcessor({
    steps: schema.steps ?? [],
    form: form as any,
    initialMeta: meta,
    getOptionProp,
    enabled: isWizard,
    onMetaUpdate,
    onStepSubmit: isWizard ? onStepSubmit : undefined,
    onSuccess,
  });

  const stepProcessor = isWizard ? stepProcessorResult : null;

  const activeEffects = useMemo(() => {
    if (isRepeatable) return [];
    if (!isWizard || !stepProcessor) return effects;
    const currentStep = stepProcessor.currentStep;
    const stepEffects = currentStep.effects ?? [];
    const stepBlockEffects = currentStep.blocks.flatMap(b => b.use.effects ?? []);
    return [...stepEffects, ...stepBlockEffects, ...(schema.effects ?? [])];
  }, [isRepeatable, isWizard, stepProcessor?.currentStep, effects, schema.effects]);

  effectsRef.current = activeEffects;
  customResolversRef.current = customResolvers as Record<string, (ctx: import('../evaluators/conditionEvaluator').ConditionContext) => unknown> | undefined;

  const activeObservers = useMemo(() => {
    if (isRepeatable) return [];
    if (!isWizard || !stepProcessor) return observers;
    const currentStep = stepProcessor.currentStep;
    const stepObservers = currentStep.observers ?? [];
    const stepBlockObservers = currentStep.blocks.flatMap(b => b.use.observers ?? []);
    return [...stepObservers, ...stepBlockObservers, ...(schema.observers ?? [])];
  }, [isRepeatable, isWizard, stepProcessor?.currentStep, observers, schema.observers]);

  useEffectProcessor({
    form: form as any,
    effects: activeEffects,
    meta,
    getOptionProp,
    customResolvers: customResolvers as any,
    hydratingRef,
    hydrationEpochRef,
    programmaticWriteGuard: programmaticWriteGuardRef,
    precisionConfig: schema.precision,
    formRules: rules,
  });

  useObserverProcessor({
    form: form as any,
    observers: activeObservers,
    meta,
    getOptionProp,
    customResolvers: customResolvers as any,
    hydratingRef,
    hydrationEpochRef,
    programmaticWriteGuard: programmaticWriteGuardRef,
    precisionConfig: schema.precision,
  });

  const { handleSubmit: lifecycleSubmit } = useLifecycle({
    form: form as any,
    mode,
    modeName,
    schemaId: schema.metadata.semanticId,
    params,
    meta,
    layout,
    namespace: defaultNamespace,
    hydratingRef,
    hydrationEpochRef,
    dispatch,
    onSuccess,
    onError,
    onFatalError,
    initialValues,
    supplementalFetch,
    onHydrated: runDerivedSync,
    onSubmitSuccess,
    onSubmitFailure,
    eventScope,
    formSemanticId: semanticId,
  });

  const onFormSubmitInternal = useCallback(
    async (values: Record<string, unknown>): Promise<unknown> => {
      onSubmitCapture?.({
        values,
        originalValues: (form.formState.defaultValues ?? {}) as Record<string, unknown>,
        dirtyFields: form.formState.dirtyFields as Record<string, boolean | Record<string, unknown>>,
      });
      const result = await lifecycleSubmit(values);
      const submitBehavior =
        mode.lifecycle?.submitBehavior ?? (mode.dataSource?.fetch ? 'close' : 'reset');
      if (submitBehavior === 'clearForm') {
        schedulePostSubmitFormReset(form, baseDefaultValues, {
          hydratingRef,
          hydrationEpochRef,
          triggerTimerRef,
        });
      }
      return result;
    },
    [form, lifecycleSubmit, onSubmitCapture, mode, baseDefaultValues, hydratingRef, hydrationEpochRef],
  );

  const onFormSubmit = useCallback(
    async (e?: React.BaseSyntheticEvent) => {
      await form.handleSubmit(
        async (values) => {
          try {
            await onFormSubmitInternal(values);
          } catch {
            // lifecycleProcessor already handles all error display (toasts, inline errors).
            // The re-throw is only for external callers (wizard orchestrator) to detect failure.
          }
        },
        (errors) => {
          const errorKeys = flattenErrorKeys(errors);
          const firstKey = errorKeys[0];
          if (!firstKey) return;
          const el =
            document.querySelector(`[name="${firstKey}"]`) ??
            document.getElementById(firstKey);
          if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            requestAnimationFrame(() => {
              if (el instanceof HTMLElement) el.focus({ preventScroll: true });
            });
          }
        },
      )(e);
    },
    [form, onFormSubmitInternal]
  );

  const stepLayout = useMemo(() => {
    if (!stepProcessor) return null;
    const currentStepBlocks = stepProcessor.currentStep.blocks;
    const merged = mergeBlocks(currentStepBlocks, params?.id);
    return merged.layout;
  }, [stepProcessor?.currentStep, params?.id]);

  const mergedCustomRenderers = useMemo(
    () => ({ ...defaultCustomRenderers, ...customRenderers }),
    [customRenderers],
  );

  return {
    form,
    layout,
    effects,
    observers,
    status: engineState.status,
    dispatch,
    meta,
    isReadOnly,
    viewPresentation,
    isWizard,
    modeName,
    modeOverrides: mode.overrides,
    getOptionProp,
    setOptionMeta,
    customRenderers: mergedCustomRenderers,
    onFormSubmit,
    onFormSubmitInternal,
    stepProcessor,
    stepLayout,
    mode,
    udfRequiredFieldsRef,
    blockOverrides,
    formHistory: schema.history?.enabled ? formHistory : null,
    namespace: defaultNamespace,
    hydratingRef,
    hydrationEpochRef,
    programmaticWriteGuardRef,
    formSessionKey,
  };
}

/** @internal — exported only for unit tests. Do not use in application code. */
export { collectFieldDefaults as __test__collectFieldDefaults };
